import Axios from 'axios';
import { call, fork, put, take, takeEvery } from 'redux-saga/effects';
import {
  FETCH_SEGMENTS,
  GET_SCREENING_PROGRESS,
  PROCEED_BULK_SCREENING,
  PROCEED_SCREENING,
  SCAN_UPLOADED_FILE,
  STOP_BULK_SCREENING,
  FETCH_MATCHING_CONFIGS,
  bulkScreeningFail,
  fetchSegmentsListFail,
  fetchSegmentsListSuccess,
  getScreeningProgress as getScreeningProgressAction,
  getScreeningProgressFail,
  getScreeningProgressSuccess,
  proceedScreeningFail,
  proceedScreeningSuccess,
  scanUploadedFileFailed,
  scanUploadedFileSuccess,
  GET_OD_NS_HISTORY_DOWNLOAD_CSV,
  getODNSHistoryCsvDownloadSuccess,
  getODNSHistoryCsvDownloadFail,
  setScreeningPercent,
  fetchMatchingConfigsSuccess,
  fetchMatchingConfigsFail,
  RESET_OD_NS_HISTORY_DOWNLOAD_CSV,
  stopBulkScreeningFail,
  stopBulkScreeningSuccess
} from '../../redux/actions/ns/onDemandScreening';
import {
  fetchODNSHistoryCsvDownload,
  fetchSegmentsList,
  getScreeningProgress,
  proceedBulkScreening,
  proceedScreening,
  uploadFile,
  fetchMatchingConfigs,
  cancelODNSHistoryCsvDownload,
  stopScreening
} from '../../services/ns/onDemandScreening';
import { END, delay, eventChannel } from 'redux-saga';

function* fetchSegmentsListWorker() {
  try {
    const { segments } = yield call(fetchSegmentsList);
    yield put(fetchSegmentsListSuccess(segments));
  } catch (error) {
    yield put(fetchSegmentsListFail(error.message));
  }
}

export function* watchGetSegmentsList() {
  yield takeEvery(FETCH_SEGMENTS, function* () {
    yield fork(fetchSegmentsListWorker);
  });
}

function* proceedScreeningWorker({ screeningData }) {
  try {
    const { alerts } = yield call(proceedScreening, { screeningData });
    if (alerts?.[0]?.is_hit && alerts[0]?.alert_id) {
      yield delay(10000);
    }
    yield put(
      proceedScreeningSuccess({
        alertId: alerts?.[0]?.is_hit ? alerts[0]?.alert_id : null
      })
    );
  } catch (error) {
    yield put(proceedScreeningFail(error.message));
  }
}

export function* watchProceedScreening() {
  yield takeEvery(
    PROCEED_SCREENING,
    function* ({ payload: { screeningData } }) {
      yield fork(proceedScreeningWorker, { screeningData });
    }
  );
}

function createUploader(file) {
  let emit;
  const progressChannel = eventChannel((emitter) => {
    emit = emitter;
    return () => {};
  });

  const uploadPromise = uploadFile(file, (percentCompleted) => {
    emit(percentCompleted);
    if (percentCompleted == 80) emit(END);
    else emit(percentCompleted);
  });

  return [uploadPromise, progressChannel];
}

function* watchOnProgress(progressChannel) {
  while (true) {
    const percent = yield take(progressChannel);
    yield put(setScreeningPercent(percent));
  }
}

function* scanUploadedFileWorker({ file }) {
  const [uploadPromise, progressChannel] = createUploader(file);
  yield fork(watchOnProgress, progressChannel);

  try {
    const {
      jobId,
      totalCount,
      rowParsingErrors = [],
      customersWithErrors = []
    } = yield call(() => uploadPromise);
    yield put(
      scanUploadedFileSuccess({
        jobId,
        totalCount,
        invalidData: [...customersWithErrors, ...rowParsingErrors]
      })
    );
  } catch (error) {
    if (!Axios.isCancel(error)) {
      yield put(scanUploadedFileFailed(error.message));
    }
  }
}

export function* watchScanUploadedFile() {
  yield takeEvery(SCAN_UPLOADED_FILE, function* ({ payload: { file } }) {
    yield fork(scanUploadedFileWorker, { file });
  });
}

function* ODNSHistoryCsvFileDownloadWorker(payload) {
  try {
    const response = yield call(fetchODNSHistoryCsvDownload, payload);
    yield put(getODNSHistoryCsvDownloadSuccess(response));
  } catch (error) {
    if (!Axios.isCancel(error)) {
      yield put(getODNSHistoryCsvDownloadFail({ error: error.message }));
    }
  }
}

export function* watchODNSHistoryCsvFileDownload() {
  yield takeEvery(GET_OD_NS_HISTORY_DOWNLOAD_CSV, function* ({ payload }) {
    yield fork(ODNSHistoryCsvFileDownloadWorker, payload);
  });
}

function* proceedBulkScreeningWorker({ jobId }) {
  try {
    yield call(proceedBulkScreening, { jobId });
    yield put(getScreeningProgressAction({ jobId }));
  } catch (error) {
    if (!Axios.isCancel(error)) {
      yield put(bulkScreeningFail(error.message));
    }
  }
}

export function* watchProceedBulkScreening() {
  yield takeEvery(PROCEED_BULK_SCREENING, function* ({ payload: { jobId } }) {
    yield fork(proceedBulkScreeningWorker, { jobId });
  });
}

function* getScreeningProgressWorker({ jobId }) {
  try {
    const { processedCustomersCount, uploadedCustomersSuccessCount } =
      yield call(getScreeningProgress, { jobId });

    if (processedCustomersCount >= uploadedCustomersSuccessCount) {
      yield put(
        getScreeningProgressSuccess({
          screenedRecordCount: processedCustomersCount || 0
        })
      );
    } else {
      let delayMilliSeconds = 500;

      if (
        uploadedCustomersSuccessCount >= 1000 &&
        uploadedCustomersSuccessCount <= 10000
      ) {
        delayMilliSeconds = uploadedCustomersSuccessCount * 0.5;
      } else if (uploadedCustomersSuccessCount > 10000) {
        delayMilliSeconds = 5000;
      }

      yield put(
        setScreeningPercent(
          Math.round(
            (processedCustomersCount * 100) / uploadedCustomersSuccessCount
          )
        )
      );
      yield delay(delayMilliSeconds);
      yield put(getScreeningProgressAction({ jobId }));
    }
  } catch (error) {
    if (!Axios.isCancel(error)) {
      yield put(getScreeningProgressFail(error.message));
    }
  }
}

export function* watchGetScreeningProgress() {
  yield takeEvery(GET_SCREENING_PROGRESS, function* ({ payload: { jobId } }) {
    yield fork(getScreeningProgressWorker, { jobId });
  });
}

function* stopBulkScreeningWorker({ jobId }) {
  try {
    yield call(stopScreening, { jobId });
    yield put(stopBulkScreeningSuccess());
  } catch (error) {
    yield put(stopBulkScreeningFail(error.message));
  }
}

export function* watchStopBulkScreening() {
  yield takeEvery(STOP_BULK_SCREENING, function* ({ payload: { jobId } }) {
    yield fork(stopBulkScreeningWorker, { jobId });
  });
}

function* fetchMatchingConfigsWorker({ runInstanceId }) {
  try {
    const data = yield call(fetchMatchingConfigs, { runInstanceId });
    yield put(fetchMatchingConfigsSuccess(data));
  } catch (error) {
    yield put(fetchMatchingConfigsFail(error.message));
  }
}

export function* watchGetMatchingConfigs() {
  yield takeEvery(
    FETCH_MATCHING_CONFIGS,
    function* ({ payload: { runInstanceId } }) {
      yield fork(fetchMatchingConfigsWorker, { runInstanceId });
    }
  );
}

function* cancelHistoryDownloadWorker() {
  yield call(cancelODNSHistoryCsvDownload);
}

export function* watchCancelHistoryDownload() {
  yield takeEvery(RESET_OD_NS_HISTORY_DOWNLOAD_CSV, function* () {
    yield fork(cancelHistoryDownloadWorker);
  });
}
